import React, { useEffect, useState } from 'react';
import { SafeHTML } from '../safe-html';
/**
 * Tells DOMPurify to preserve SVG markup (elements plus presentation attributes
 * such as `fill`, `stroke`, `viewBox`). Verified to keep
 * `fill="var(--rd-sys-color-*)"` values intact, which is what lets an inlined
 * illustration adapt to the active (light/dark) theme.
 */
const SVG_SANITIZE_CONFIG = {
    USE_PROFILES: { svg: true, svgFilters: true },
};
// Fetch each unique SVG only once across the app, and reuse the markup on
// subsequent renders so there is no repeated network cost or flash.
const svgMarkupCache = new Map();
const fetchSvgMarkup = (path) => {
    // No fetch in SSR / some test environments — render nothing rather than
    // throwing. Callers that mock fetch will still resolve normally.
    if (typeof fetch === 'undefined') {
        return Promise.resolve('');
    }
    if (!svgMarkupCache.has(path)) {
        svgMarkupCache.set(path, fetch(path)
            .then((response) => (response.ok ? response.text() : ''))
            .catch(() => ''));
    }
    return svgMarkupCache.get(path);
};
/**
 * Fetches an SVG file and renders its markup inline in the document, instead of
 * via an `<img src>` tag. Inlining is required so that CSS custom properties
 * referenced inside the SVG (e.g. `--rd-sys-color-content-tertiary`) resolve
 * against the host page and follow the active theme — an `<img>`-embedded SVG
 * is CSS-isolated and cannot read them.
 */
export const InlineSvg = ({ path, className }) => {
    const [markup, setMarkup] = useState('');
    useEffect(() => {
        let isMounted = true;
        // Reset while the (possibly different) path loads so we never show a
        // stale illustration.
        setMarkup('');
        if (!path) {
            return undefined;
        }
        fetchSvgMarkup(path)
            .then((text) => {
            if (isMounted) {
                setMarkup(text);
            }
            return text;
        })
            .catch(() => {
            // fetchSvgMarkup already swallows fetch errors (resolves to
            // ''); nothing to recover from here.
        });
        return () => {
            isMounted = false;
        };
    }, [path]);
    // While the fetch is pending, SafeHTML renders an empty (but classed, hence
    // sized) wrapper, avoiding layout shift.
    return (React.createElement(SafeHTML, { className: className, html: markup, config: SVG_SANITIZE_CONFIG }));
};
//# sourceMappingURL=inline-svg.js.map